'use client';

import { useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import { FiClock, FiMic } from 'react-icons/fi';
import { BiSolidCheckCircle } from 'react-icons/bi';
import { HiOutlineDocumentText, HiOutlineLightBulb } from 'react-icons/hi';
import { HiOutlineChatBubbleLeft, HiOutlineSparkles } from 'react-icons/hi2';
import CTABanner from '@/components/blocks/CTABanner';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';
import SiteFrequentlyAskedQuestionsComponent from '@/components/frequently-asked-questions/SiteFrequentlyAskedQuestionsComponent';
import ReactHtmlParser from 'react-html-parser';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const voicemailBenefits = [
    {
      icon: FiClock,
      title: t('Voicemail/why_voicemail/points/point1/title'),
      description: t('Voicemail/why_voicemail/points/point1/description'),
    },
    {
      icon: HiOutlineChatBubbleLeft,
      title: t('Voicemail/why_voicemail/points/point2/title'),
      description: t('Voicemail/why_voicemail/points/point2/description'),
    },
    {
      icon: BiSolidCheckCircle,
      title: t('Voicemail/why_voicemail/points/point3/title'),
      description: t('Voicemail/why_voicemail/points/point3/description'),
    },
    {
      icon: HiOutlineSparkles,
      title: t('Voicemail/why_voicemail/points/point4/title'),
      description: t('Voicemail/why_voicemail/points/point4/description'),
    },
  ];

  const howItWorksSteps = [
    {
      title: t('Voicemail/how_it_works/steps/step1/title'),
      description: t('Voicemail/how_it_works/steps/step1/description'),
      image: 'https://cdn.1cdn.app/application/LOUISEBOT/25011412154971_Options-min.png',
    },
    {
      title: t('Voicemail/how_it_works/steps/step2/title'),
      description: t('Voicemail/how_it_works/steps/step2/description'),
      image: 'https://cdn.1cdn.app/application/LOUISEBOT/25011412155046_Condition-min.png',
    },
    {
      title: t('Voicemail/how_it_works/steps/step3/title'),
      description: t('Voicemail/how_it_works/steps/step3/description'),
      image: 'https://cdn.1cdn.app/application/LOUISEBOT/25011412154978_Test-min.png',
    },
  ];

  const leadFormAdvantages = [
    {
      icon: HiOutlineLightBulb,
      title: t('Voicemail/lead_form_explanation/why_its_powerful/arg1/title'),
      description: t('Voicemail/lead_form_explanation/why_its_powerful/arg1/description'),
    },
    {
      icon: HiOutlineDocumentText,
      title: t('Voicemail/lead_form_explanation/why_its_powerful/arg2/title'),
      description: t('Voicemail/lead_form_explanation/why_its_powerful/arg2/description'),
    },
    {
      icon: HiOutlineChatBubbleLeft,
      title: t('Voicemail/lead_form_explanation/why_its_powerful/arg3/title'),
      description: t('Voicemail/lead_form_explanation/why_its_powerful/arg3/description'),
    },
  ];

  const essentialReasons = [
    {
      icon: HiOutlineSparkles,
      title: t('Voicemail/why_essential/points/point1/title'),
      description: t('Voicemail/why_essential/points/point1/description'),
    },
    {
      icon: HiOutlineDocumentText,
      title: t('Voicemail/why_essential/points/point2/title'),
      description: t('Voicemail/why_essential/points/point2/description'),
    },
    {
      icon: BiSolidCheckCircle,
      title: t('Voicemail/why_essential/points/point3/title'),
      description: t('Voicemail/why_essential/points/point3/description'),
    },
  ];

  return (
    <AnimatedGradientWrapper>
      <div className="tw-mb-4">
        <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <div className="tw-container tw-mx-auto tw-max-w-7xl">
            {/* Header */}
            <div className="tw-text-center tw-mb-16">
              <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
                <FiMic className="tw-w-4 tw-h-4" />
                {t('Voicemail/badge')}
              </div>

              <h1 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-font-['Lexend']">
                {t('Voicemail/header/title')}
              </h1>

              <p className="tw-text-lg tw-text-gray-600 tw-max-w-2xl tw-mx-auto tw-font-['Lexend']">
                {t('Voicemail/header/description')}
              </p>
            </div>

            {/* Main Image */}
            <div className="tw-flex tw-justify-center tw-items-center tw-w-full tw-mb-16">
              <div className="tw-rounded-3xl tw-overflow-hidden tw-shadow-2xl tw-group tw-transition-all tw-duration-300 hover:tw-shadow-3xl">
                <Image
                  priority
                  quality={100}
                  src="https://cdn.1cdn.app/application/LOUISEBOT/25080908382190_voicemail_img.png"
                  alt="LouiseBot Voicemail"
                  width={800}
                  height={400}
                  className="tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
                />
              </div>
            </div>

            {/* Why use voicemail? */}
            <div className="tw-mb-16 lg:tw-mb-24">
              <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-8 tw-font-['Lexend'] tw-text-center">
                {t('Voicemail/why_voicemail/title')}
              </h2>

              <div className="tw-grid md:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6">
                {voicemailBenefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="tw-bg-white tw-rounded-2xl tw-p-6 tw-border tw-border-gray-200 tw-shadow-lg tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-105 tw-group"
                  >
                    <div className="tw-flex tw-flex-col tw-items-center tw-mb-4">
                      <div className="tw-w-16 tw-h-16 tw-bg-blue-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-3">
                        <benefit.icon className="tw-w-8 tw-h-8 tw-text-blue-600" />
                      </div>
                    </div>
                    <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-3 tw-font-['Lexend'] tw-text-center">
                      {benefit.title}
                    </h3>
                    <p className="tw-text-gray-600 tw-font-['Lexend'] tw-text-center tw-text-sm">
                      {benefit.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* How does it work? */}
            <div className="tw-mb-16 lg:tw-mb-24">
              <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-8 tw-font-['Lexend'] tw-text-center">
                {t('Voicemail/how_it_works/title')}
              </h2>

              <div className="tw-grid md:tw-grid-cols-3 tw-gap-8">
                {howItWorksSteps.map((step, index) => (
                  <div
                    key={index}
                    className="tw-bg-white tw-rounded-2xl tw-p-6 tw-border tw-border-gray-200 tw-shadow-lg tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-105 tw-group tw-relative"
                  >
                    <div className="tw-absolute tw-top-3 tw-left-3 tw-w-8 tw-h-8 tw-bg-blue-600 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-sm">
                      {index + 1}
                    </div>
                    <div className="tw-flex tw-items-center tw-justify-center tw-mb-4">
                      <Image
                        src={step.image}
                        alt={step.title}
                        width={100}
                        height={100}
                        className="tw-object-cover tw-rounded-lg tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
                      />
                    </div>
                    <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-font-['Lexend'] tw-text-center">
                      {step.title}
                    </h3>
                    <p className="tw-text-gray-600 tw-font-['Lexend'] tw-text-center">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Lead Form Explanation */}
            <div className="tw-mb-16 lg:tw-mb-24">
              <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-font-['Lexend'] tw-text-center">
                {t('Voicemail/lead_form_explanation/title')}
              </h2>
              <p className="tw-text-lg tw-text-gray-600 tw-max-w-4xl tw-mx-auto tw-font-['Lexend'] tw-text-center tw-mb-12">
                {t('Voicemail/lead_form_explanation/description')}
              </p>

              <div className="tw-grid md:tw-grid-cols-3 tw-gap-8">
                {leadFormAdvantages.map((advantage, index) => (
                  <div
                    key={index}
                    className="tw-bg-white tw-rounded-2xl tw-p-6 tw-border tw-border-gray-200 tw-shadow-lg tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-105 tw-group"
                  >
                    <div className="tw-flex tw-items-center tw-mb-4">
                      <div className="tw-w-16 tw-h-16 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-4">
                        <advantage.icon className="tw-w-8 tw-h-8 tw-text-green-600" />
                      </div>
                    </div>
                    <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-font-['Lexend']">
                      {advantage.title}
                    </h3>
                    <p className="tw-text-gray-600 tw-font-['Lexend']">{advantage.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Use Case */}
            <div className="tw-mb-16 lg:tw-mb-24">
              <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-8 tw-font-['Lexend'] tw-text-center">
                {t('Voicemail/use_case/title')}
              </h2>

              <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-purple-50 tw-rounded-3xl tw-p-8 tw-border tw-border-gray-200">
                <div className="tw-mb-8">
                  <p className="tw-text-center tw-text-lg tw-text-gray-700 tw-font-['Lexend'] tw-max-w-4xl tw-mx-auto">
                    {ReactHtmlParser(t('Voicemail/use_case/description'))}
                  </p>
                  <p className="tw-text-base tw-text-gray-700 tw-font-['Lexend'] tw-max-w-4xl tw-mx-auto tw-mt-4">
                    {ReactHtmlParser(t('Voicemail/use_case/sub_description'))}
                  </p>
                </div>

                <div className="tw-grid md:tw-grid-cols-3 tw-gap-6">
                  <div className="tw-bg-white tw-rounded-xl tw-p-6 tw-text-center">
                    <FiMic className="tw-w-12 tw-h-12 tw-text-blue-600 tw-mx-auto tw-mb-4" />
                    <h4 className="tw-font-bold tw-text-gray-900 tw-mb-2 tw-font-['Lexend']">
                      {t('Voicemail/use_case/result/voice_message')}
                    </h4>
                    <p className="tw-text-sm tw-text-gray-600 tw-font-['Lexend']">
                      {t('Voicemail/use_case/result/user_message')}
                    </p>
                  </div>

                  <div className="tw-bg-white tw-rounded-xl tw-p-6 tw-text-center">
                    <HiOutlineDocumentText className="tw-w-12 tw-h-12 tw-text-purple-600 tw-mx-auto tw-mb-4" />
                    <h4 className="tw-font-bold tw-text-gray-900 tw-mb-2 tw-font-['Lexend']">
                      {t('Voicemail/use_case/result/lead_form_title')}
                    </h4>
                    <p className="tw-text-sm tw-text-gray-600 tw-font-['Lexend']">
                      {t('Voicemail/use_case/result/lead_form')}
                    </p>
                  </div>

                  <div className="tw-bg-white tw-rounded-xl tw-p-6 tw-text-center">
                    <BiSolidCheckCircle className="tw-w-12 tw-h-12 tw-text-green-600 tw-mx-auto tw-mb-4" />
                    <h4 className="tw-font-bold tw-text-gray-900 tw-mb-2 tw-font-['Lexend']">
                      {t('Voicemail/use_case/result/business_benefit_message')}
                    </h4>
                    <p className="tw-text-sm tw-text-gray-600 tw-font-['Lexend']">
                      {t('Voicemail/use_case/result/business_benefit')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why it's essential */}
            <div className="tw-mb-16 lg:tw-mb-24">
              <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-8 tw-font-['Lexend'] tw-text-center">
                {t('Voicemail/why_essential/title')}
              </h2>

              <div className="tw-grid md:tw-grid-cols-3 tw-gap-8">
                {essentialReasons.map((reason, index) => (
                  <div
                    key={index}
                    className="tw-bg-white tw-rounded-2xl tw-p-6 tw-border tw-border-gray-200 tw-shadow-lg tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-105 tw-group"
                  >
                    <div className="tw-flex tw-items-center tw-mb-4">
                      <div className="tw-w-16 tw-h-16 tw-bg-orange-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-4">
                        <reason.icon className="tw-w-8 tw-h-8 tw-text-orange-600" />
                      </div>
                    </div>
                    <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-font-['Lexend']">
                      {reason.title}
                    </h3>
                    <p className="tw-text-gray-600 tw-font-['Lexend']">{reason.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <CTABanner
          cta={t('Voicemail/closing/cta')}
          ctaTarget="register"
          title={t('Voicemail/closing/description')}
          locale={locale}
        />

        <div className="tw-mt-20">
          <SiteFrequentlyAskedQuestionsComponent locale={locale} session="VoicemailFAQs" />
        </div>
      </div>
    </AnimatedGradientWrapper>
  );
}
